import React, { useState, useEffect } from 'react';
import { Sync, MoreVert } from '@mui/icons-material';
import { DynamicContainer } from '../components/common/DynamicContainer';
// import { generateBreadcrumbPath } from './common/breadcrumbs'; // Commented out - breadcrumbs not needed
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
// testing
import {
  IconButton,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Menu,
  ListItemText,
} from '@mui/material';
import SyncFlowParameter from './SyncFlowDrawer/SyncFlowParameter';
import TrackingRecordModal from './TrackingRecordModal';
import { useUserRights } from '../context/UserRightsContext';
import {
  isMfUiActionEnabled,
  uiConfigForMasterFlow,
} from '../services/masterFlowUiConfig';
import { fetchTenantLicensedFlowUi } from '../services/licensingApi';

// Import flow handlers
import { handleCostXToQuickbooksFlow } from './Flows/CostXToQuickbooksFlow';
import { handleQuickbooksCustomerToAutodeskCompanyFlow } from './Flows/QuickbooksCustomerToAutodeskCompany';
import { handleQuickbooksVendorToAutodeskCompanyFlow } from './Flows/QuickbooksVendorToAutodeskCompany';
import { handleAutodeskBudgetToQuickbooksItemFlow } from './Flows/AutodeskBudgetToQuickbooksItem';
import { handleAutodeskContractToQuickbooksPurchaseOrderFlow } from './Flows/AutodeskContractToQuickbooksPurchaseOrder';
import { handleAutodeskCostToQuickbooksBillFlow } from './Flows/AutodeskCostToQuickbooksBill';
import { handleAutodeskExpenseToQuickbooksBillFlow } from './Flows/AutodeskExpenseToQuickbooksBill';
import { handleAutodeskBudgetPaymentToQuickbookFlow } from './Flows/AutodeskBudgetPaymentToQuickbook';
import { handleAutodeskProjectAndQbProjectsWithSyncProject } from './Flows/AutodeskProjectAndQbProjectsWithSyncProjec';
import { handleSalesforceQuotesToFusionOperationSalesOrderFlow } from './Flows/SalesforceQuotesToFusionOperationSalesOrder';
import { handleFusion360BOMToFusionOperationProductionOrderFlow } from './Flows/Fusion360BOMToFusionOperationProductionOrder';
import { handleFusionOperationPurchaseOrderDynamicsPurchaseOrderFlow } from './Flows/FusionOperationPurchaseOrderDynamicsPurchaseOrder';
import { handleFusionOperationItemsToDynamicsFinishedProductFlow } from './Flows/FusionOperationItemsToDynamicsFinishedProduct';
import { handleQuickbooksExpenseToAutodeskExpenseFlow } from './Flows/QuickbooksExpenseToAutodeskExpense';
import { handleAutodeskVaultToFusionOperationProducts } from './Flows/AutodeskVaultToFusionOperationProducts';
import { handleAutodeskVaultFileUploadToFusionOperationProducts } from './Flows/AutodeskVaultFileUploadToFusionoperationProducts';
import { handleAutodeskVaultToUnanetProducts } from './Flows/AutodeskVaultToUnanetProducts';
import { handleAutodeskDocsToSharePointFlow } from './Flows/AutodeskDocsToSharePoint';
import { handleAutodeskVaultToUnanetProductsSync } from './Flows/AutodeskVaultToUnanetProductsSync';
import { handleAutodeskVaultToUnanetBOMSync } from './Flows/AutodeskVaultToUnanetBOMSync';
import { handleAutodeskDocsToGoogleDriveFlow } from './Flows/AutodeskDocsToGoogleDrive';
import { handleAutodeskDocsCopyFilesFlow } from './Flows/AutodeskDocsCopyFiles';
import { handleBcCustomerToAutodeskCompanyFlow } from './Flows/BcCustomerToAutodeskCompany';
import { handleBcVendorToAutodeskCompanyFlow } from './Flows/BcVendorToAutodeskCompany';
import { handleBcItemToFusionProductFlow } from './Flows/BcItemToFusionProduct';
import { handleAutodeskBudgetPaymentToDynamicsSalesInvoiceFlow } from './Flows/AutodeskBudgetPaymentToDynamicsSalesInvoice';
import { handleSageIntacctJournalToSnowflakeFlow } from './Flows/SageIntacctJournalToSnowflakeFlow';
import { handleConductorGlToSageJournalEntryFlow } from './Flows/ConductorGlToSageJournalEntryFlow';
import { handleConductorInvoiceToQboPurchaseFlow } from './Flows/ConductorInvoiceToQboPurchaseFlow';
import { handleConductorInvoiceToSageBillFlow } from './Flows/ConductorInvoiceToSageBillFlow';
import { handleSageVendorToConductorVendorFlow } from './Flows/SageVendorToConductorVendorFlow';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-flow-instances`;
const MASTER_FLOW_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/master-flow`;
const MASTER_CONNECTOR_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/master-connector`;
const SYNC_RUN_STATUS_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/api/sync/runs`;
const CONTAINER_ASSIGNMENTS_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/api/sync-container-assignments`;
const ASSIGNED_CONTAINERS_API_URL = `${CONTAINER_ASSIGNMENTS_API_URL}/sync-file`;
const FLOW_STATISTICS_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-tracks/flow-statistics`;
const CONNECTOR_ASSIGNMENT_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/flow-connector-assignment`;

// Get token from localStorage
const token = localStorage.getItem('token');
const tenantId = localStorage.getItem('tenantId');

// Create a custom component for the trigger type selector
const TriggerTypeSelector = ({ value, onChange, error }) => {
  const parseInitialValue = () => ({
    triggerType: value?.triggerType || 'Manual',
    intervalValue: value?.intervalValue || '',
    timeValue: value?.timeValue || '',
  });

  const [state, setState] = useState(parseInitialValue());

  useEffect(() => {
    setState(parseInitialValue());
  }, [value]);

  const handleTriggerChange = (event) => {
    const newTriggerType = event.target.value;
    setState((prev) => ({ ...prev, triggerType: newTriggerType }));
    onChange({
      triggerType: newTriggerType,
      intervalValue: state.intervalValue,
      timeValue: state.timeValue,
    });
  };

  const handleIntervalChange = (event) => {
    const newInterval = event.target.value;
    setState((prev) => ({ ...prev, intervalValue: newInterval }));
    onChange({
      triggerType: state.triggerType,
      intervalValue: newInterval,
      timeValue: state.timeValue,
    });
  };

  const handleTimeChange = (event) => {
    const newTime = event.target.value;
    setState((prev) => ({ ...prev, timeValue: newTime }));
    onChange({
      triggerType: state.triggerType,
      intervalValue: state.intervalValue,
      timeValue: newTime,
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      <FormControl fullWidth margin="dense" error={!!error} size="small">
        <InputLabel id="trigger-type-label">Trigger Type</InputLabel>
        <Select
          labelId="trigger-type-label"
          value={state.triggerType || 'Manual'}
          label="Trigger Type"
          onChange={handleTriggerChange}
          variant="outlined"
        >
          <MenuItem value="Manual">Manual</MenuItem>
          <MenuItem value="Minutes">Minutes</MenuItem>
          <MenuItem value="Time">Time</MenuItem>
        </Select>
      </FormControl>
      {state.triggerType === 'Minutes' && (
        <TextField
          label="Minutes Interval"
          value={state.intervalValue}
          onChange={handleIntervalChange}
          type="number"
          fullWidth
          margin="dense"
          size="small"
          variant="outlined"
          helperText="Enter minutes between executions (e.g., 30)"
          inputProps={{ min: 1, step: 1 }}
          error={state.triggerType === 'Minutes' && !state.intervalValue}
          required
        />
      )}
      {state.triggerType === 'Time' && (
        <TextField
          label="Scheduled Time"
          value={state.timeValue}
          onChange={handleTimeChange}
          type="time"
          fullWidth
          margin="dense"
          size="small"
          variant="outlined"
          helperText="Enter time in HH:MM format (e.g., 14:30)"
          error={state.triggerType === 'Time' && !state.timeValue}
          required
        />
      )}
    </Box>
  );
};

const SyncFlow = () => {
  const navigate = useNavigate();
  const { hasFullAccess, canExecute } = useUserRights();
  const allowExecute = hasFullAccess || canExecute('Sync Flow');
  const [isLoading, setIsLoading] = useState(false);
  const [syncFlows, setSyncFlows] = useState([]);
  const [masterFlowOptions, setMasterFlowOptions] = useState([]);
  const [masterFlowsData, setMasterFlowsData] = useState([]);
  const [rawSyncFlowData, setRawSyncFlowData] = useState([]);
  const [masterConnectors, setMasterConnectors] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedFlowDetails, setSelectedFlowDetails] = useState(null);
  const [executingFlow, setExecutingFlow] = useState(false);
  const [paramDrawerOpen, setParamDrawerOpen] = useState(false);
  const [trackingModalOpen, setTrackingModalOpen] = useState(false);
  const [selectedFlowForTracking, setSelectedFlowForTracking] = useState(null);
  const [runningFlowIds, setRunningFlowIds] = useState(new Set()); // Track multiple running flows
  const [flowTokenStatus, setFlowTokenStatus] = useState({}); // Store token status for each flow
  const [trackingRecordsCounts, setTrackingRecordsCounts] = useState({}); // Store tracking records counts for each flow
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedRowForMenu, setSelectedRowForMenu] = useState(null);
  const [licensedFlowUiByMfId, setLicensedFlowUiByMfId] = useState({});

  const normalizeConnectorName = (name) =>
    (name || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, '');

  /** Canonical connector type (same as backend ConnectorType.getMcName()) for assignment matching. */
  const getCanonicalTypeFromMcConnectorType = (mcConnectorType) => {
    if (!mcConnectorType || typeof mcConnectorType !== 'string') return null;
    const normalized = mcConnectorType.toUpperCase().trim().replace(/\s+/g, '_');
    const map = {
      QUICKBOOKS: 'QuickBooks',
      AUTODESK: 'Autodesk',
      FUSION_OPERATION: 'Fusion Operation',
      DYNAMICS_BUSINESS_CENTRAL: 'Dynamics Business Central',
      VAULT: 'Autodesk Vault',
      AUTODESK_DOCS: 'Autodesk Docs',
      SALESFORCE: 'Salesforce',
      UNANET: 'Unanet',
      GOOGLE_DRIVE: 'GoogleDrive',
      SAGE_INTACCT: 'Sage Intacct',
      SNOWFLAKE: 'Snowflake',
      SHAREPOINT: 'SharePoint',
      MICROSOFT_TEAMS: 'Microsoft Teams',
      MINIO_STORAGE: 'MinIO Storage',
      AWS_S3_STORAGE: 'AWS S3 Storage',
      EGNYTE_STORAGE: 'Egnyte Storage',
      GOOGLE_DRIVE_STORAGE: 'Google Drive Storage',
      SHAREPOINT_STORAGE: 'SharePoint Storage',
      DROPBOX_STORAGE: 'Dropbox Storage',
    };
    return map[normalized] || null;
  };

  const normalizeFlowStatus = (response) => {
    if (!response || typeof response !== 'object' || Array.isArray(response)) {
      return null;
    }
    const { status } = response;
    if (typeof status !== 'string') {
      return null;
    }
    return status.trim().toLowerCase();
  };

  /** Maps technical error messages to user-friendly text. */
  const toUserFriendlyErrorMessage = (message) => {
    if (!message || typeof message !== 'string') return message;
    const lower = message.toLowerCase();
    if (lower.includes('disconnected') || lower.includes('no active (connected)')) {
      return message;
    }
    if (lower.includes('failed to retrieve connection') || lower.includes('connection id') || lower.includes('no connector assigned') || lower.includes('no connector found')) {
      return 'Please assign the required connectors for this flow in the flow settings, then try again.';
    }
    if (lower.includes('no parameters found') || lower.includes('missing required') || lower.includes('mandatory fields missing') || lower.includes('required fields') || lower.includes('please provide all required')) {
      return 'Please configure the required parameters for this flow in the flow settings, then try again.';
    }
    if (lower.includes('instance id is required')) {
      return 'This flow could not be run. Please open the flow from the Sync Flow list and try again.';
    }
    if (lower.includes('no container') || lower.includes('container assignment')) {
      return 'Please assign at least one container to this flow in the flow settings, then try again.';
    }
    return message;
  };

  /** Returns { success, errorMessage } from flow API response (200 with errorCount/results). */
  const getFlowRunResult = (response) => {
    if (!response || typeof response !== 'object' || Array.isArray(response)) {
      return { success: true };
    }
    const errorCount = response.errorCount ?? 0;
    const results = Array.isArray(response.results) ? response.results : [];
    const hasErrorResult = results.some((r) => r && (r.status === 'ERROR' || r.status === 'error'));
    if (errorCount > 0 || hasErrorResult) {
      const firstError = results.find((r) => r && (r.status === 'ERROR' || r.status === 'error'));
      const rawMessage = firstError?.errorMessage || firstError?.message || response.message || 'Sync completed with errors.';
      const errorMessage = toUserFriendlyErrorMessage(rawMessage);
      return { success: false, errorMessage };
    }
    return { success: true };
  };

  const getFlowResponseMessage = (response, flowName) => {
    if (response && typeof response === 'object' && !Array.isArray(response)) {
      if (typeof response.message === 'string' && response.message.trim().length > 0) {
        return response.message;
      }
      if (response.status) {
        return `${flowName || 'Flow'} completed with status "${response.status}"`;
      }
    }
    return `${flowName || 'Flow'} completed with warnings`;
  };

  const getRequiredConnectorIds = (masterFlowId) => {
    if (!masterFlowId) {
      return [];
    }
    const flowRecord = masterFlowsData.find((flow) => flow.mfId === masterFlowId);
    if (!flowRecord) {
      return [];
    }

    const ids = [flowRecord.mfnSourceAppId, flowRecord.mfnTargetAppId]
      .filter(Boolean);

    return [...new Set(ids)];
  };

  const getConnectorDescriptor = (connectorId) => {
    if (!connectorId) {
      return null;
    }

    const connector = masterConnectors.find((mc) => mc.mcId === connectorId);
    if (!connector?.mcName) {
      return null;
    }

    const mcType = connector.mcConnectorType ?? connector.mc_connector_type;
    const canonicalType = getCanonicalTypeFromMcConnectorType(mcType);

    return {
      id: connectorId,
      name: connector.mcName,
      canonicalType: canonicalType || connector.mcName,
    };
  };

  const fetchConnectorAssignmentsForFlow = async (masterFlowId, instanceId = null) => {
    const url = instanceId
      ? `${CONNECTOR_ASSIGNMENT_API_URL}/flow/${masterFlowId}?instanceId=${instanceId}`
      : `${CONNECTOR_ASSIGNMENT_API_URL}/flow/${masterFlowId}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      const message = errorText || `Failed to validate connector assignments (status ${response.status})`;
      throw new Error(message);
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  };

  const validateConnectorAssignments = async (syncFlow) => {
    const masterFlowId = syncFlow.masterFlowId || syncFlow.sfl_mf_id;
    if (!masterFlowId) {
      return;
    }

    const requiredConnectorIds = getRequiredConnectorIds(masterFlowId);
    if (!requiredConnectorIds.length) {
      return;
    }

    if (!masterConnectors.length) {
      console.warn('Master connectors not yet loaded. Skipping UI connector validation for flow', masterFlowId);
      return;
    }

    const requiredConnectors = requiredConnectorIds
      .map(getConnectorDescriptor)
      .filter(Boolean);

    if (!requiredConnectors.length) {
      return;
    }

    const instanceId = syncFlow.instanceId ?? syncFlow.sfiId ?? null;
    const assignments = await fetchConnectorAssignmentsForFlow(masterFlowId, instanceId);

    const missingConnectors = requiredConnectors
      .filter((connector) => !assignments.some((assignment) => {
        if (!assignment.connectorType || !assignment.connectorId) {
          return false;
        }
        const assignmentNorm = normalizeConnectorName(assignment.connectorType);
        const canonicalNorm = normalizeConnectorName(connector.canonicalType);
        const nameNorm = normalizeConnectorName(connector.name);
        return assignmentNorm === canonicalNorm || assignmentNorm === nameNorm;
      }))
      .map((connector) => connector.name);

    if (missingConnectors.length) {
      throw new Error(`Assign connector(s) ${missingConnectors.join(', ')} before running this flow.`);
    }

    const disconnectedConnectors = requiredConnectors
      .filter((connector) => {
        const assignment = assignments.find((candidate) => {
          if (!candidate.connectorType || !candidate.connectorId) {
            return false;
          }
          const assignmentNorm = normalizeConnectorName(candidate.connectorType);
          const canonicalNorm = normalizeConnectorName(connector.canonicalType);
          const nameNorm = normalizeConnectorName(connector.name);
          return assignmentNorm === canonicalNorm || assignmentNorm === nameNorm;
        });
        return assignment
          && String(assignment.connectorStatus || '').trim().toLowerCase() !== 'connected';
      })
      .map((connector) => connector.name);

    if (disconnectedConnectors.length) {
      throw new Error(
        `${disconnectedConnectors.join(', ')} connector is assigned but Disconnected. `
        + 'Please reconnect it in the Connector Token screen before running this flow.'
      );
    }
  };

  // Check if user is CDX user
  const isCdxUser = () => {
    try {
      const cdxUser = localStorage.getItem('cdx_user');
      return cdxUser === 'true';
    } catch (error) {
      console.error('Error reading cdx_user from localStorage:', error);
      return false;
    }
  };

  // Fetch token statuses for all flows
  const fetchTokenStatuses = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/tenant/${tenantId}/token-status`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const statusData = await response.json();
        console.log('Token status data:', statusData);
        setFlowTokenStatus(statusData);
      }
    } catch (error) {
      console.error('Error fetching token statuses:', error);
    }
  };

  // Fetch flow statistics for processed and failed counts
  const fetchFlowStatistics = async () => {
    if (syncFlows.length === 0) return;

    try {
      const response = await fetch(FLOW_STATISTICS_API_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Flow statistics response:', data);
      console.log('Available sync flows:', syncFlows.map(f => ({ sfl_id: f.sfl_id, sfl_mf_id: f.sfl_mf_id, name: f.sfl_mf_name })));

      // Create a map of flow statistics by flowId
      const statisticsMap = {};
      data.forEach(stat => {
        statisticsMap[stat.flowId] = {
          processed: stat.processedCount,
          failed: stat.failedCount
        };
      });
      console.log('Statistics map:', statisticsMap);

      // Map statistics to sync flows: API returns flowId = instance id (or master when no instance).
      // For flows with instanceId, use only that key so multiple instances of the same master show their own counts.
      const defaultStats = { processed: 0, failed: 0 };
      const countsMap = {};
      syncFlows.forEach(flow => {
        const stats = flow.instanceId
          ? (statisticsMap[flow.instanceId] || defaultStats)
          : (statisticsMap[flow.sfl_id] || statisticsMap[flow.sfl_mf_id] || defaultStats);
        countsMap[flow.sfl_id] = stats;
        console.log(`Mapping flow ${flow.sfl_mf_name} (sfl_mf_id: ${flow.sfl_mf_id}) -> stats:`, stats);
      });

      setTrackingRecordsCounts(countsMap);
    } catch (error) {
      console.error('Error fetching flow statistics:', error);
      // Set default values for all flows in case of error
      const countsMap = {};
      syncFlows.forEach(flow => {
        countsMap[flow.sfl_id] = { processed: 0, failed: 0 };
      });
      setTrackingRecordsCounts(countsMap);
    }
  };

  // Fetch master flow data and connectors
  useEffect(() => {
    fetchMasterFlowOptions();
    fetchMasterConnectors();
  }, []);

  // Per licensed master flow UI from License (⋮ / Properties for this tenant)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!tenantId || tenantId === 'ALL') {
        setLicensedFlowUiByMfId({});
        return;
      }
      try {
        const byMfId = await fetchTenantLicensedFlowUi(tenantId);
        if (!cancelled) setLicensedFlowUiByMfId(byMfId || {});
      } catch {
        if (!cancelled) setLicensedFlowUiByMfId({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // Fetch sync flows once master flow data is available
  useEffect(() => {
    if (masterFlowsData.length > 0) {
      fetchSyncFlows();
      fetchTokenStatuses(); // Fetch token statuses when flows are loaded
      
      // Set up silent polling to automatically update grid for scheduled/manual flow results
      const pollInterval = setInterval(() => {
        fetchSyncFlows(true);
      }, 10000);

      return () => clearInterval(pollInterval);
    }
  }, [masterFlowsData]);

  // Re-map sync flow data
  useEffect(() => {
    if (rawSyncFlowData.length > 0 && masterFlowsData.length > 0) {
      mapSyncFlowsData();
    }
  }, [rawSyncFlowData, masterFlowsData]);

  // Fetch flow statistics when syncFlows change
  useEffect(() => {
    if (syncFlows.length > 0) {
      fetchFlowStatistics();
    }
  }, [syncFlows]);

  const fetchMasterConnectors = async () => {
    try {
      console.log('Fetching master connectors...');
      const authToken = localStorage.getItem('token');
      const response = await fetch(MASTER_CONNECTOR_API_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Master connectors response:', data);
      setMasterConnectors(data);
    } catch (error) {
      console.error('Error fetching master connectors:', error);
      toast.error(`Error loading master connectors: ${error.message}`);
    }
  };

  // Check if containers are assigned for container-specific flows
  // Check if containers are assigned for container-specific flows
  const checkContainersAssigned = async (flowId) => {
    try {
      console.log('Checking containers for flow ID:', flowId);
      const response = await fetch(`${ASSIGNED_CONTAINERS_API_URL}/${flowId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        // If API returns 404, it means no containers are assigned
        if (response.status === 404) {
          console.log('No container assignments found (404) for flow ID:', flowId);
          return false;
        }
        // For other errors, log but don't block execution
        console.warn('Failed to check container assignments, status:', response.status, 'for flow ID:', flowId);
        const errorText = await response.text().catch(() => '');
        console.warn('Error response:', errorText);
        // Assume containers might exist (don't block execution)
        return true;
      }

      const data = await response.json();
      console.log('Container assignments response for flow ID', flowId, ':', data);
      const hasContainers = Array.isArray(data) && data.length > 0;
      console.log('Has containers:', hasContainers);
      return hasContainers;
    } catch (error) {
      // If check fails, log error but don't block execution (might be network issue)
      console.error('Error checking container assignments for flow ID', flowId, ':', error);
      // Assume containers might exist to avoid blocking execution
      return true;
    }
  };

  // List of container-specific flows that require containers to be assigned
  const containerSpecificFlows = [
    'Autodesk Cost Payment To QuickBooks Bill',
    'Autodesk Expense To QuickBooks Expense',
    'Autodesk Budget Payment To QuickBooks Invoice',
    'Autodesk Budget To QuickBooks Item',
    'Autodesk Contract To QuickBooks Purchase Order',
    'Quickbooks Expense To Autodesk Expense',
    'Autodesk Budget Payment to Dynamics Sales Invoice'
  ];

  const executeSyncFlow = async (syncFlow) => {
    let pollInterval = null;
    let flowResponse = null;
    try {
      // Check if this flow is already running
      if (isFlowRunning(syncFlow.instanceId)) {
        toast.warning('This flow is already running.');
        return;
      }
      
      setExecutingFlow(true);
      await validateConnectorAssignments(syncFlow);
      // Add this flow to running flows
      setRunningFlowIds(prev => new Set([...prev, syncFlow.instanceId]));

      console.log('Executing flow:', syncFlow.sfl_mf_name);

      // Check if this is a container-specific flow and validate containers
      // Check if this is a container-specific flow and validate containers
      if (containerSpecificFlows.includes(syncFlow.sfl_mf_name)) {
        const instanceId = syncFlow.instanceId;
        if (!instanceId) {
          setExecutingFlow(false);
          setRunningFlowIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(syncFlow.instanceId);
            return newSet;
          });
          toast.warning('Flow instance ID is required. Please run from a valid flow row.');
          return;
        }
        const hasContainers = await checkContainersAssigned(instanceId);
        if (!hasContainers) {
          setExecutingFlow(false);
          setRunningFlowIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(syncFlow.instanceId);
            return newSet;
          });
          toast.warning('Please assign at least one container to this flow before executing. You can assign containers in the flow settings.');
          return;
        }
      }

      // Start polling for token status updates during execution
      pollInterval = setInterval(() => {
        fetchTokenStatuses();
      }, 2000); // Poll every 2 seconds

      switch (syncFlow.sfl_mf_name) {
        case 'CostX Estimate To Quickbooks Estimate':
          flowResponse = await handleCostXToQuickbooksFlow(syncFlow, masterConnectors);
          break;
        case 'QuickBooks Customer To Autodesk Company':
          flowResponse = await handleQuickbooksCustomerToAutodeskCompanyFlow(syncFlow, masterConnectors);
          break;
        case 'QuickBooks Vendor To Autodesk Company':
          flowResponse = await handleQuickbooksVendorToAutodeskCompanyFlow(syncFlow, masterConnectors);
          break;
        case 'Autodesk Budget To QuickBooks Item':
          flowResponse = await handleAutodeskBudgetToQuickbooksItemFlow(syncFlow, masterConnectors);
          break;
        case 'Autodesk Contract To QuickBooks Purchase Order':
          flowResponse = await handleAutodeskContractToQuickbooksPurchaseOrderFlow(syncFlow, masterConnectors);
          break;
        case 'Autodesk Cost Payment To QuickBooks Bill':
          flowResponse = await handleAutodeskCostToQuickbooksBillFlow(syncFlow, masterConnectors);
          break;
        case 'Autodesk Expense To QuickBooks Expense':
          flowResponse = await handleAutodeskExpenseToQuickbooksBillFlow(syncFlow, masterConnectors);
          break;
        case 'Autodesk Budget Payment To QuickBooks Invoice':
          flowResponse = await handleAutodeskBudgetPaymentToQuickbookFlow(syncFlow, masterConnectors);
          break;
        case 'Autodesk and QB Projects Sync':
          flowResponse = await handleAutodeskProjectAndQbProjectsWithSyncProject(syncFlow, masterConnectors);
          break;
        case 'Salesforce Quotes To Fusion Operation Sales Order':
          flowResponse = await handleSalesforceQuotesToFusionOperationSalesOrderFlow(syncFlow, masterConnectors);
          break;
        case 'Fusion 360 BOM To Fusion Operation Production Order':
          flowResponse = await handleFusion360BOMToFusionOperationProductionOrderFlow(syncFlow, masterConnectors);
          break;
        case 'Fusion Operation Purchase Order To Dynamics Purchase Order':
          flowResponse = await handleFusionOperationPurchaseOrderDynamicsPurchaseOrderFlow(syncFlow, masterConnectors);
          break;
        case 'Fusion Operation Items To Dynamics Finished Product':
          flowResponse = await handleFusionOperationItemsToDynamicsFinishedProductFlow(syncFlow, masterConnectors);
          break;
        case 'QuickBooks Expense To Autodesk Expense':
          flowResponse = await handleQuickbooksExpenseToAutodeskExpenseFlow(syncFlow, masterConnectors);
          break;
        case 'Autodesk Vault To Fusion Operation Products':
          flowResponse = await handleAutodeskVaultToFusionOperationProducts(syncFlow, masterConnectors);
          break;
        case 'Autodesk Vault File Upload To Fusion Operation Products':
          flowResponse = await handleAutodeskVaultFileUploadToFusionOperationProducts(syncFlow, masterConnectors);
          break;
        case 'Autodesk Vault To Unanet Products':
          flowResponse = await handleAutodeskVaultToUnanetProducts(syncFlow, masterConnectors);
          break;
        case 'Autodesk Vault To Unanet Products Sync':
        case 'Autodesk Vault To Unanet Product Sync':
          flowResponse = await handleAutodeskVaultToUnanetProductsSync(syncFlow, masterConnectors);
          break;
        case 'Autodesk Vault To Unanet BOM Sync':
          flowResponse = await handleAutodeskVaultToUnanetBOMSync(syncFlow, masterConnectors);
          break;
        case 'Autodesk Docs To Share Point':
          flowResponse = await handleAutodeskDocsToSharePointFlow(syncFlow, masterConnectors);
          break;
        case 'Autodesk Docs To Google Drive':
          flowResponse = await handleAutodeskDocsToGoogleDriveFlow(syncFlow, masterConnectors);
          break;
        case 'Autodesk Docs Copy Files':
          flowResponse = await handleAutodeskDocsCopyFilesFlow(syncFlow, masterConnectors);
          break;
        case 'Dynamics BC Customer To Autodesk Company':
          flowResponse = await handleBcCustomerToAutodeskCompanyFlow(syncFlow, masterConnectors);
          break;
        case 'Dynamics BC Vendor To Autodesk Company':
          flowResponse = await handleBcVendorToAutodeskCompanyFlow(syncFlow, masterConnectors);
          break;
        case 'Dynamics BC Item To Fusion Operation Product':
          flowResponse = await handleBcItemToFusionProductFlow(syncFlow, masterConnectors);
          break;
        case 'Autodesk Budget Payment to Dynamics Sales Invoice':
          flowResponse = await handleAutodeskBudgetPaymentToDynamicsSalesInvoiceFlow(syncFlow, masterConnectors);
          break;
        case 'Sage Intacct Journal to Snowflake':
          flowResponse = await handleSageIntacctJournalToSnowflakeFlow(syncFlow, masterConnectors);
          break;
        case 'Conductor GL to Sage Journal Entry':
        case 'Conductor GL to Sage Intacct': // legacy mf_name
          flowResponse = await handleConductorGlToSageJournalEntryFlow(syncFlow);
          break;
        case 'Conductor Invoice to QuickBooks Purchase':
          flowResponse = await handleConductorInvoiceToQboPurchaseFlow(syncFlow);
          break;
        case 'Conductor Invoices to Sage Bill':
        case 'Conductor invoices to Sage bill': // DB mf_name casing
          flowResponse = await handleConductorInvoiceToSageBillFlow(syncFlow);
          break;
        case 'Sage Vendor to Conductor Vendor':
        case 'Sage vendor to Conductor vendor':
          flowResponse = await handleSageVendorToConductorVendorFlow(syncFlow);
          break;
        default:
          clearInterval(pollInterval);
          toast.warning(`Flow type "${syncFlow.sfl_mf_name}" is not configured for execution`);
          return;
      }

      // Clear polling interval
      clearInterval(pollInterval);

      if (syncFlow.sfl_mf_name === 'Autodesk and QB Projects Sync') {
        toast.success('Sync flow executed successfully');
      } else {
        const runResult = getFlowRunResult(flowResponse);
        if (!runResult.success) {
          toast.error(runResult.errorMessage || 'Sync flow completed with errors.');
        } else {
          const flowStatus = normalizeFlowStatus(flowResponse);
          if (!flowStatus || flowStatus === 'success') {
            toast.success('Sync flow executed successfully');
          }
        }
      }
      // Final refresh of token statuses after execution
      await fetchTokenStatuses();
      await fetchSyncFlows(true);
    } catch (error) {
      // Clear polling interval on error
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      console.error('Error executing sync flow:', error);

      // Show toast for connector/parameter/configuration errors with user-friendly message.
      // Prefer concrete backend message over generic API/500 bucket.
      const errorMessage = (error?.message || '').trim();
      if (errorMessage) {
        const lower = errorMessage.toLowerCase();
        const isConnectorError = lower.includes('assign connector') || lower.includes('connector not assigned') ||
          lower.includes('no connector found for this flow') || lower.includes('please assign connectors') || lower.includes('connection id');
        const isParamError = lower.includes('no parameters found') || lower.includes('instance id is required') ||
          lower.includes('mandatory fields missing') || lower.includes('required fields') || lower.includes('please provide all required');
        const isGenericServerError = lower.includes('internal server error') || lower.includes('api error') || lower.includes('500');

        // Extract backend detail from strings like: "API Error 500: <backend message>"
        const apiErrorMatch = errorMessage.match(/^api error\s+\d+\s*:\s*(.+)$/i);
        const backendDetail = apiErrorMatch?.[1]?.trim();
        const hasSpecificBackendDetail = Boolean(backendDetail && backendDetail.length > 0);

        if (isConnectorError || isParamError) {
          toast.error(toUserFriendlyErrorMessage(errorMessage));
        } else if (hasSpecificBackendDetail) {
          toast.error(toUserFriendlyErrorMessage(backendDetail));
        } else if (isGenericServerError) {
          toast.error('Please configure the required parameters for this flow in the flow settings, then try again.');
        } else {
          toast.error(toUserFriendlyErrorMessage(errorMessage));
        }
      }
      // Refresh token statuses even on error to show if token issue occurred
      await fetchTokenStatuses();
      await fetchSyncFlows(true);
    } finally {
      setExecutingFlow(false);
      // Remove this flow from running flows
      setRunningFlowIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(syncFlow.instanceId);
        return newSet;
      });
    }
  };

  const updateSyncFlowStatus = async (instanceId, status, syncFlowData) => {
    try {
      const rawSyncFlow = rawSyncFlowData.find((flow) => flow.sfiId === instanceId);
      if (!rawSyncFlow) {
        throw new Error('Sync flow instance not found in raw data');
      }

      const editedSyncFlow = syncFlows.find((flow) => flow.instanceId === instanceId);
      if (!editedSyncFlow) {
        throw new Error('Sync flow instance not found in edited data');
      }

      const updateData = {
        sfiMfId: rawSyncFlow.sfiMfId,
        sfiTriggerType: editedSyncFlow.sfiTriggerType || rawSyncFlow.sfiTriggerType || 'Manual',
        sfiInterval: editedSyncFlow.sfiTriggerType === 'Minutes' ? parseInt(editedSyncFlow.sfiInterval, 10) || null : null,
        sfiTime: editedSyncFlow.sfiTriggerType === 'Time' ? editedSyncFlow.sfiTime || null : null,
        sfiDescription: editedSyncFlow.sfl_description || rawSyncFlow.sfiDescription || '',
        sfiStatus: status,
      };

      console.log('Updating sync flow status with data:', updateData);

      const response = await fetch(`${API_BASE_URL}/${instanceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      await fetchSyncFlows();

      if (drawerOpen && selectedFlowDetails && selectedFlowDetails.instanceId === instanceId) {
        setSelectedFlowDetails({
          ...selectedFlowDetails,
          status: status,
          sfiTriggerType: updateData.sfiTriggerType,
          sfiInterval: updateData.sfiInterval,
          sfiTime: updateData.sfiTime,
          sfl_description: updateData.sfiDescription,
        });
      }
    } catch (error) {
      console.error('Error updating sync flow status:', error);
      toast.error(`Error updating sync flow status: ${error.message}`);
    }
  };

  const fetchMasterFlowOptions = async () => {
    setIsLoading(true);
    try {
      console.log('Fetching master flow options...');
      // Auth Bearer is enough — backend resolves tenant from logged-in user (g_tenant).
      const authToken = localStorage.getItem('token');
      const response = await fetch(MASTER_FLOW_API_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Master flow options response:', data);
      setMasterFlowsData(data);
      const options = data.map((flow) => flow.mfName || flow.mfName).filter(Boolean);
      console.log('Master flow options extracted:', options);
      setMasterFlowOptions(options);
    } catch (error) {
      console.error('Error fetching master flow options:', error);
      toast.error(`Error loading master flow options: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getMasterFlowIdByName = (masterFlowFunction) => {
    const masterFlow = masterFlowsData.find(
      (flow) =>
        (flow.mfFlowFunction?.toLowerCase() === masterFlowFunction?.toLowerCase()) ||
        (flow.mfName?.toLowerCase() === masterFlowFunction?.toLowerCase())
    );
    return masterFlow ? masterFlow.mfId : null;
  };

  const getMasterFlowNameById = (masterFlowId) => {
    const masterFlow = masterFlowsData.find((flow) => flow.mfId === masterFlowId);
    return masterFlow ? (masterFlow.mfName || masterFlow.mfName) : 'Unknown';
  };

  const fetchSyncFlows = async (silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      console.log('Fetching sync flows...');
      const response = await fetch(`${API_BASE_URL}/tenant/${tenantId}/group/SyncFlow?page=0&size=1000`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Response:', data);
      
      // Handle paginated response: { content, total, page, pageSize }
      const isPaginated = data && typeof data === 'object' && Array.isArray(data.content);
      const list = isPaginated ? data.content : (Array.isArray(data) ? data : []);
      
      // Data is already filtered by backend to only include SyncFlow group
      setRawSyncFlowData(list);
      return list;
    } catch (error) {
      console.error('Error details:', error);
      toast.error(`Error fetching sync flows: ${error.message}`);
      return [];
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const mapSyncFlowsData = () => {
    console.log('Mapping sync flows with master flows data...');
    const mappedData = rawSyncFlowData.map((item) => {
      const recId = item.recId || '';
      const instanceId = item.sfiId || '';
      const masterFlowId = item.sfiMfId || '';  // This is the actual ID
      const description = item.sfiDescription || '';
      const triggerType = item.sfiTriggerType || 'Manual';
      const interval = item.sfiInterval || '';
      const time = item.sfiTime || '';
      const status = item.sfiStatus || 'Not Started';

      const masterFlowFunction = getMasterFlowNameById(masterFlowId);  // This is the name

      let timeDisplay = '';
      if (triggerType === 'Minutes' && interval) {
        timeDisplay = `${interval} minutes`;
      } else if (triggerType === 'Time' && time) {
        timeDisplay = time;
      }

      return {
        id: recId,
        sfl_id: recId,
        sfl_mf_id: masterFlowId,  // Store the actual ID here
        sfl_mf_name: masterFlowFunction,  // Store the name separately
        sfiTriggerType: triggerType,
        sfiInterval: interval,
        sfiTime: time,
        sfl_duration: timeDisplay,
        sfl_description: description,
        status: status,
        display_trigger_type: triggerType,
        instanceId: instanceId,
        masterFlowId: masterFlowId,  // Keep this for reference
        sfi_group: item.sfiGroup || 'SyncFlow',  // Add group field
      };
    });

    console.log('Mapped data for grid:', mappedData);
    setSyncFlows(mappedData);
  };

  const handleCreate = async (data) => {
    try {
      const masterFlowId = getMasterFlowIdByName(data.sfl_mf_id);
      if (!masterFlowId) {
        throw new Error('Invalid master flow selection');
      }

      const triggerData = data.sfiTriggerType || { triggerType: 'Manual', intervalValue: '', timeValue: '' };
      const backendData = {
        sfiMfId: masterFlowId,
        sfiGroup: 'SyncFlow', // Set internally - always 'SyncFlow' for Sync Flow page
        sfiTriggerType: triggerData.triggerType || 'Manual',
        sfiInterval: triggerData.triggerType === 'Minutes' ? parseInt(triggerData.intervalValue, 10) || null : null,
        sfiTime: triggerData.triggerType === 'Time' ? triggerData.timeValue || null : null,
        sfiDescription: data.sfl_description || '',
        sfiStatus: 'Not Started',
      };

      console.log('Creating sync flow with data:', backendData);

      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(backendData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || `HTTP error! status: ${response.status}, message: ${errorText}`;
        } catch (e) {
          errorMessage = `HTTP error! status: ${response.status}, message: ${errorText}`;
        }
        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      console.log('Create response:', responseData);
      toast.success('Sync flow created successfully');
      await fetchSyncFlows();
      return responseData;
    } catch (error) {
      console.error('Create error full details:', error);
      toast.error(`Error creating sync flow: ${error.message}`);
      throw error;
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      console.log('Form data received:', data);
      console.log('Master flow options:', masterFlowOptions);
      console.log('Master flows data:', masterFlowsData);

      if (masterFlowsData.length === 0) {
        throw new Error('Master flow data not loaded yet');
      }

      const masterFlowId = getMasterFlowIdByName(data.sfl_mf_id);
      if (!masterFlowId) {
        throw new Error(`Invalid master flow selection: ${data.sfl_mf_id}`);
      }

      const syncFlowInstance = syncFlows.find((flow) => flow.sfl_id.toString() === id.toString());
      if (!syncFlowInstance || !syncFlowInstance.instanceId) {
        throw new Error('Instance UUID not found for the given recId');
      }

      const existingFlow = rawSyncFlowData.find((flow) => flow.sfiId === syncFlowInstance.instanceId);
      if (!existingFlow) {
        throw new Error('Existing flow data not found');
      }

      const triggerData = data.sfiTriggerType || { triggerType: 'Manual', intervalValue: '', timeValue: '' };
      const backendData = {
        sfiMfId: masterFlowId,
        sfiGroup: existingFlow.sfiGroup || 'SyncFlow', // Keep existing group or default to 'SyncFlow'
        sfiTriggerType: triggerData.triggerType || existingFlow.sfiTriggerType || 'Manual',
        sfiInterval:
          triggerData.triggerType === 'Minutes'
            ? parseInt(triggerData.intervalValue, 10) || null
            : null,
        sfiTime: triggerData.triggerType === 'Time' ? triggerData.timeValue || null : null,
        sfiDescription: data.sfl_description || existingFlow.sfiDescription || '',
        sfiStatus: existingFlow.sfiStatus || 'Not Started',
      };

      console.log('Updating sync flow with data:', backendData);

      const response = await fetch(`${API_BASE_URL}/${syncFlowInstance.instanceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(backendData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || `HTTP error! status: ${response.status}, message: ${errorText}`;
        } catch (e) {
          errorMessage = `HTTP error! status: ${response.status}, message: ${errorText}`;
        }
        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      console.log('Update response:', responseData);

      // Update rawSyncFlowData to reflect the new interval
      setRawSyncFlowData((prev) =>
        prev.map((flow) =>
          flow.sfiId === syncFlowInstance.instanceId
            ? { ...flow, ...backendData }
            : flow
        )
      );

      toast.success('Sync flow updated successfully');

      if (drawerOpen && selectedFlowDetails && selectedFlowDetails.instanceId === syncFlowInstance.instanceId) {
        setSelectedFlowDetails({
          ...selectedFlowDetails,
          ...backendData,
          sfl_mf_id: data.sfl_mf_id,
          status: backendData.sfiStatus,
        });
      }

      await fetchSyncFlows();
      return responseData;
    } catch (error) {
      console.error('Update error full details:', error);
      toast.error(`Error updating sync flow: ${error.message}`);
      throw error;
    }
  };

  const handleDelete = async (id) => {
    try {
      const syncFlowInstance = syncFlows.find((flow) => flow.sfl_id.toString() === id.toString());
      if (!syncFlowInstance || !syncFlowInstance.instanceId) {
        throw new Error('Instance UUID not found for the given recId');
      }

      if (drawerOpen && selectedFlowDetails && selectedFlowDetails.instanceId === syncFlowInstance.instanceId) {
        setDrawerOpen(false);
        setSelectedFlowDetails(null);
      }

      console.log('Deleting sync flow:', syncFlowInstance.instanceId);

      const response = await fetch(`${API_BASE_URL}/${syncFlowInstance.instanceId}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast.success('Sync flow deleted successfully');
      await fetchSyncFlows();
      return true;
    } catch (error) {
      console.error('Delete error full details:', error);
      toast.error(`Error deleting sync flow: ${error.message}`);
      throw error;
    }
  };

  const handleOpenDetail = (flow) => {
    setSelectedFlowDetails(flow);
    setParamDrawerOpen(true);
  };

  const handleCloseParamDrawer = () => {
    setParamDrawerOpen(false);
  };

  const handleRowDoubleClick = (params) => {
    setSelectedFlowForTracking(params.row);
    setTrackingModalOpen(true);
  };

  const handleCloseTrackingModal = () => {
    setTrackingModalOpen(false);
    setSelectedFlowForTracking(null);
  };

  // Check if a specific flow is running
  const isFlowRunning = (instanceId) => {
    return runningFlowIds.has(instanceId) ||
      syncFlows.find(flow => flow.instanceId === instanceId)?.status === 'Running';
  };

  // More options menu column
  const moreOptionsColumn = {
    field: 'moreOptions',
    headerName: '',
    width: 60,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      return (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchorEl(e.currentTarget);
            setSelectedRowForMenu(params.row);
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      );
    },
  };

  // Menu handlers
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedRowForMenu(null);
  };

  const handleDataPointClick = (flowRow) => {
    if (!flowRow || !flowRow.instanceId) {
      toast.error('Invalid flow selected');
      return;
    }

    console.log('Navigating to Data Point for flow:', flowRow);

    // Navigate to data point page with flow information
    navigate(`/datapoint/${flowRow.instanceId}`, {
      state: {
        flowInstanceId: flowRow.instanceId,
        masterFlowId: flowRow.masterFlowId,
        flowName: flowRow.sfl_mf_name,
        tenantId: tenantId
      }
    });
  };

  const handleMenuItemClick = (action) => {
    if (!selectedRowForMenu) return;

    switch (action) {
      case 'Manual Run':
        if (!allowExecute) {
          toast.error('No Execute permission for Sync Flow');
          break;
        }
        executeSyncFlow(selectedRowForMenu);
        break;
      case 'Properties':
        // Open parameters drawer (same as info button functionality)
        handleOpenDetail(selectedRowForMenu);
        break;
      case 'Tracking Records':
        // Open tracking records modal (same as double-click functionality)
        setSelectedFlowForTracking(selectedRowForMenu);
        setTrackingModalOpen(true);
        break;
      case 'Data Point':
        handleDataPointClick(selectedRowForMenu);
        break;
      default:
        break;
    }

    handleMenuClose();
  };

  const columns = [
    { field: 'sfl_mf_name', headerName: 'Sync Flow', width: 500 },
    { field: 'display_trigger_type', headerName: 'Trigger Type', width: 150 },
    { field: 'sfl_duration', headerName: 'Time', width: 120 },
    { field: 'sfl_description', headerName: 'Description', width: 300 },
    {
      field: 'flowStatus',
      headerName: 'Flow Status',
      width: 140,
      renderCell: (params) => {
        // Back-end now enriches sfiStatus with latest sync_run_status.srs_status for this instance.
        // In our mapped grid row we store it in `status`, so fall back to that.
        const rawStatus = params.row.sfiStatus || params.row.status || 'Not Started';
        const status = rawStatus.toUpperCase();

        let color = '#9e9e9e'; // default grey
        let label = rawStatus || 'Not Started';

        switch (status) {
          case 'RUNNING':
            color = '#4caf50'; // green (treat running as good/active)
            break;
          case 'COMPLETED':
          case 'SUCCESS':
          case 'INTEGRATED':
            color = '#4caf50'; // green
            label = 'SUCCESS';
            break;
          case 'FAILED':
            color = '#f44336'; // red
            label = 'FAILED';
            break;
          case 'SCHEDULED':
          case 'PENDING':
            color = '#2196f3'; // blue
            label = 'SCHEDULED';
            break;
          default:
            color = '#9e9e9e';
        }

        return <span style={{ color, fontWeight: 'bold' }}>{label}</span>;
      },
    },
    {
      field: 'processed',
      headerName: 'Processed',
      width: 100,
      renderCell: (params) => {
        const counts = trackingRecordsCounts[params.row.sfl_id];
        const processedCount = counts?.processed || 0;
        return <span style={{ color: '#4CAF50', fontWeight: 'bold' }}>{processedCount}</span>;
      },
    },
    {
      field: 'failed',
      headerName: 'Failed',
      width: 100,
      renderCell: (params) => {
        const counts = trackingRecordsCounts[params.row.sfl_id];
        const failedCount = counts?.failed || 0;
        return <span style={{ color: '#F44336', fontWeight: 'bold' }}>{failedCount}</span>;
      },
    },
    moreOptionsColumn,
  ];

  const formFields = [
    {
      name: 'sfl_mf_id',
      label: 'Master Flow',
      required: true,
      type: 'select',
      width: 6,
      options: masterFlowOptions.map((name) => ({ value: name, label: name })),
      validate: (value) => (!value ? 'Master Flow is required' : null),
    },
    {
      name: 'sfiTriggerType',
      label: 'Trigger Type',
      required: true,
      type: 'custom',
      width: 6,
      component: TriggerTypeSelector,
      validate: (value) =>
        !value?.triggerType
          ? 'Trigger Type is required'
          : value.triggerType === 'Minutes' && !value.intervalValue
            ? 'Interval is required for Minutes trigger type'
            : value.triggerType === 'Time' && !value.timeValue
              ? 'Time is required for Time trigger type'
              : null,
    },
    {
      name: 'sfl_description',
      label: 'Description',
      multiline: true,
      rows: 3,
      width: 12,
    },
  ];

  const initialFormData = {
    sfl_id: '',
    instanceId: '',
    sfl_mf_id: '',
    sfiTriggerType: { triggerType: 'Manual', intervalValue: '', timeValue: '' },
    sfl_description: '',
  };

  // const breadcrumbPaths = generateBreadcrumbPath('syncflow'); // Commented out - breadcrumbs not needed

  return (
    <>
      <DynamicContainer
        title="Sync Flow"
        icon={Sync}
        columns={columns}
        formFields={formFields}
        initialFormData={initialFormData}
        idField="sfl_id"
        // breadcrumbPaths={breadcrumbPaths} // Commented out - breadcrumbs not needed
        rows={syncFlows}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onRowDoubleClick={handleRowDoubleClick}
        isLoading={isLoading}
        isCdxUser={isCdxUser()}
        functionCode="Sync Flow"
      />
      {selectedFlowDetails && (
        <SyncFlowParameter
          open={paramDrawerOpen}
          onClose={handleCloseParamDrawer}
          masterFlowId={selectedFlowDetails.sfl_mf_id}
          masterFlowName={selectedFlowDetails.sfl_mf_name}
          instanceId={selectedFlowDetails.instanceId}
          uiConfig={uiConfigForMasterFlow(masterFlowsData, selectedFlowDetails.sfl_mf_id, {
            hasFullAccess,
            licensedFlowUiByMfId,
          })}
        />
      )}
      <TrackingRecordModal
        open={trackingModalOpen}
        onClose={handleCloseTrackingModal}
        flowData={selectedFlowForTracking}
        isFlowRunning={selectedFlowForTracking ? isFlowRunning(selectedFlowForTracking.instanceId) : false}
      />

      {/* More Options Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {(() => {
          const menuUi = uiConfigForMasterFlow(
            masterFlowsData,
            selectedRowForMenu?.sfl_mf_id || selectedRowForMenu?.masterFlowId,
            { hasFullAccess, licensedFlowUiByMfId }
          );
          return (
            <>
              {isMfUiActionEnabled(menuUi, 'trackingRecords', { hasFullAccess }) && (
                <MenuItem onClick={() => handleMenuItemClick('Tracking Records')}>
                  <ListItemText>Tracking Records</ListItemText>
                </MenuItem>
              )}
              {isMfUiActionEnabled(menuUi, 'dataPoint', { hasFullAccess }) && (
                <MenuItem onClick={() => handleMenuItemClick('Data Point')}>
                  <ListItemText>Data Point</ListItemText>
                </MenuItem>
              )}
              {isMfUiActionEnabled(menuUi, 'properties', { hasFullAccess }) && (
                <MenuItem onClick={() => handleMenuItemClick('Properties')}>
                  <ListItemText>Properties</ListItemText>
                </MenuItem>
              )}
              {isMfUiActionEnabled(menuUi, 'manualRun', { hasFullAccess }) && (
                <MenuItem
                  onClick={() => handleMenuItemClick('Manual Run')}
                  disabled={!allowExecute}
                >
                  <ListItemText>
                    {allowExecute ? 'Manual Run' : 'Manual Run (no Execute permission)'}
                  </ListItemText>
                </MenuItem>
              )}
            </>
          );
        })()}
      </Menu>
    </>
  );
};

export default SyncFlow;